import { createCalendar } from "@internationalized/date";
import { type AriaDateFieldOptions, useDateField } from "@react-aria/datepicker";
import { useLocale } from "@react-aria/i18n";
import { type DateFieldStateOptions, useDateFieldState } from "@react-stately/datepicker";
import cx from "classnames";
import { type FC, useRef } from "react";
import { type DateValue } from "react-aria";

import { datePickerGroupClassname } from "../DatePicker/common";
import { makePointerStyles, makeTextStyles } from "../utilities";
import { DateSegment } from "./DateSegment";

export type InputDateSize = "lg" | "md" | "sm";

export type InputDateProps = Omit<
	DateFieldStateOptions,
	"locale" | "createCalendar" | "isDisabled"
> &
	Omit<AriaDateFieldOptions<DateValue>, "isDisabled"> & {
		size?: InputDateSize;
		disabled?: boolean;
		type?: "standalone" | "embedded";
		constantWidthDateSegments?: boolean;
	};

export const InputDate: FC<InputDateProps> = ({
	size = "sm",
	disabled = false,
	type = "standalone",
	constantWidthDateSegments = true,
	...props
}) => {
	props = Object.assign(props, { isDisabled: disabled });

	const { locale } = useLocale();
	const state = useDateFieldState({
		...props,
		locale,
		createCalendar,
	});

	const ref = useRef<HTMLDivElement>(null);
	const { labelProps, fieldProps } = useDateField(props, state, ref);

	return (
		<div className="flex flex-col items-start space-y-1">
			{props.label && (
				<span
					{...labelProps}
					className={cx(makeTextStyles(disabled, size), makePointerStyles(disabled))}
				>
					{props.label}
				</span>
			)}
			<div
				{...fieldProps}
				ref={ref}
				className={cx(
					"flex",
					"transition-colors",
					"bg-primary",
					"ring-default focus-within:ring-4",
					makePointerStyles(disabled),
					datePickerGroupClassname(disabled, size, type),
					{
						"pr-2": size === "sm",
						"pr-3": size === "md" || size === "lg",
					}
				)}
			>
				<div className="flex items-center">
					{state.segments.map((segment, i) => (
						<DateSegment
							datePickerDialogTrigger="manual"
							triggerDatePickerDialog={undefined}
							key={i}
							segment={segment}
							state={state}
							constantWidth={constantWidthDateSegments}
						/>
					))}
				</div>
			</div>
		</div>
	);
};
